import * as Inovx from '@shared';
import ReactViewer3DConstants from './ReactViewer3DConstants';
import ReactViewer3DInternalConstants from './ReactViewer3DInternalConstants';
import { ContentLayers } from '../../Interfaces/ViewerInterfaces';
import * as Enumerations from '../../Enumerations/Enumerations';

var dispatcher = Inovx.Shared.Flux.AppDispatcher.get(ReactViewer3DInternalConstants.DISPATCHER_NAME);

// ---------------------------------------------------------------------
// ACK support for RV_POPULATE_ASSET_SETS -> RV_ASSET_SETS_POPULATED
// (No unregister available, so we register ONE handler and fan out ourselves.)
// ---------------------------------------------------------------------

// key: `${viewerId}:${requestId}` -> resolve()
const _assetSetsAckWaiters: { [key: string]: () => void } = {};
let _assetSetsAckListenerRegistered = false;

const _assetSetsColorizedAckWaiters: { [key: string]: () => void } = {};
let _assetSetsColorizedAckListenerRegistered = false;

const _viewerDataSetupAckWaiters: {
    [key: string]: { resolve: () => void; reject: (reason?: unknown) => void };
} = {};
let _viewerDataSetupAckListenerRegistered = false;

const _screenshotWaiters: { [key: string]: (image: string) => void } = {};
let _screenshotListenerRegistered = false;

const _panoramaWaiters: { [key: string]: (image: string) => void } = {};
let _panoramaListenerRegistered = false;

function _ensureAssetSetsAckListenerRegistered() {
    if (_assetSetsAckListenerRegistered) return;
    _assetSetsAckListenerRegistered = true;

    dispatcher.register((payload: any) => {
        if (!payload || payload.actionType !== ReactViewer3DConstants.RV_ASSET_SETS_POPULATED) return;

        const viewerId = payload.viewerId as string;
        const requestId = payload.requestId as string;

        if (!viewerId || !requestId) return;

        const key = `${viewerId}:${requestId}`;
        const resolve = _assetSetsAckWaiters[key];
        if (!resolve) return;

        delete _assetSetsAckWaiters[key];
        resolve();
    });
}

function _ensureAssetSetsColorizedAckListenerRegistered() {
    if (_assetSetsColorizedAckListenerRegistered) return;
    _assetSetsColorizedAckListenerRegistered = true;

    dispatcher.register((payload: any) => {
        if (!payload || payload.actionType !== ReactViewer3DConstants.RV_ASSET_SETS_COLORIZED) return;

        const viewerId = payload.viewerId as string;
        const requestId = payload.requestId as string;

        if (!viewerId || !requestId) return;

        const key = `${viewerId}:${requestId}`;
        const resolve = _assetSetsColorizedAckWaiters[key];
        if (!resolve) return;

        delete _assetSetsColorizedAckWaiters[key];
        resolve();
    });
}

function _ensureViewerDataSetupAckListenerRegistered() {
    if (_viewerDataSetupAckListenerRegistered) return;
    _viewerDataSetupAckListenerRegistered = true;

    dispatcher.register((payload: any) => {
        if (!payload || payload.actionType !== ReactViewer3DConstants.RV_VIEWER_DATA_SETUP) return;

        const viewerId = payload.viewerId as string;
        const requestId = payload.requestId as string;

        if (!viewerId || !requestId) return;

        const key = `${viewerId}:${requestId}`;
        const waiter = _viewerDataSetupAckWaiters[key];
        if (!waiter) return;

        delete _viewerDataSetupAckWaiters[key];
        if (payload.error) waiter.reject(payload.error);
        else waiter.resolve();
    });
}

function _ensureScreenshotListenerRegistered() {
    if (_screenshotListenerRegistered) return;
    _screenshotListenerRegistered = true;

    dispatcher.register((payload: any) => {
        if (!payload || payload.actionType !== ReactViewer3DConstants.RV_SCREENSHOT_READY) return;

        const viewerId = payload.viewerId as string;
        const requestId = payload.requestId as string;

        if (!viewerId || !requestId) return;

        const key = `${viewerId}:${requestId}`;
        const resolve = _screenshotWaiters[key];
        if (!resolve) return;

        delete _screenshotWaiters[key];
        resolve(payload.image || '');
    });
}

function _ensurePanoramaListenerRegistered() {
    if (_panoramaListenerRegistered) return;
    _panoramaListenerRegistered = true;

    dispatcher.register((payload: any) => {
        if (!payload || payload.actionType !== ReactViewer3DConstants.RV_PANORAMA_READY) return;

        const viewerId = payload.viewerId as string;
        const requestId = payload.requestId as string;

        if (!viewerId || !requestId) return;

        const key = `${viewerId}:${requestId}`;
        const resolve = _panoramaWaiters[key];
        if (!resolve) return;

        delete _panoramaWaiters[key];
        resolve(payload.image || '');
    });
}

function _makeRequestId(): string {
    // Avoid crypto dependency here; good enough uniqueness for UI ordering
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

var ReactViewer3DActions = {
    // Expose this so your ViewerActions can await it
    waitForAssetSetsPopulated(viewerId: string, requestId: string): Promise<void> {
        _ensureAssetSetsAckListenerRegistered();

        const key = `${viewerId}:${requestId}`;
        return new Promise<void>((resolve) => {
            _assetSetsAckWaiters[key] = resolve;
        });
    },
    waitForAssetSetsColorized(viewerId: string, requestId: string): Promise<void> {
        _ensureAssetSetsColorizedAckListenerRegistered();

        const key = `${viewerId}:${requestId}`;
        return new Promise<void>((resolve) => {
            _assetSetsColorizedAckWaiters[key] = resolve;
        });
    },
    waitForViewerDataSetup(viewerId: string, requestId: string): Promise<void> {
        _ensureViewerDataSetupAckListenerRegistered();

        const key = `${viewerId}:${requestId}`;
        return new Promise<void>((resolve, reject) => {
            _viewerDataSetupAckWaiters[key] = { resolve, reject };
        });
    },
    getScreenshot(viewerId: string, width?: number, height?: number, includeOverlays: boolean = true): Promise<string> {
        _ensureScreenshotListenerRegistered();

        const requestId = _makeRequestId();
        const key = `${viewerId}:${requestId}`;

        return new Promise<string>((resolve) => {
            _screenshotWaiters[key] = resolve;
            dispatcher.dispatch({
                actionType: ReactViewer3DConstants.RV_GET_SCREENSHOT,
                viewerId,
                requestId,
                width,
                height,
                includeOverlays
            });
        });
    },
    getPanoramaImage(viewerId: string, size: number): Promise<string> {
        _ensurePanoramaListenerRegistered();

        const requestId = _makeRequestId();
        const key = `${viewerId}:${requestId}`;

        return new Promise<string>((resolve) => {
            _panoramaWaiters[key] = resolve;
            dispatcher.dispatch({
                actionType: ReactViewer3DConstants.RV_GET_PANORAMA,
                viewerId,
                requestId,
                size
            });
        });
    },
    

    // -----------------------------------------------------------------
    // Existing actions
    // -----------------------------------------------------------------

    setAnnotations(viewerId: string, annotations: IAssetTag[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_ANNOTATIONS,
            viewerId: viewerId,
            annotations: annotations,
        });
    },

    reinitializeViewer(viewerId: string, ontologyId: string, projectId: string, ontologyBoundsContext?: IOntologyBoundsContext, rangePosition?: IVector3D, rangeDistance?: number, assetSets?: IAssetSet[], cameraPosition?: IVector3D, cameraDirection?: IVector3D, focalDistance? : number, repositionCamera? :boolean, requestId?: string) {
        const rid = requestId || _makeRequestId();
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_RANGE_POSITION,
            viewerId: viewerId,
            ontologyId: ontologyId,
            projectId: projectId,
            ontologyBoundsContext: ontologyBoundsContext,
            rangePosition: rangePosition,
            rangeDistance: rangeDistance,
            assetSets: assetSets,
            cameraDirection: cameraDirection,
            cameraPosition: cameraPosition,
            focalDistance: focalDistance,
            repositionCamera: repositionCamera,
            requestId: rid
        });
        return rid;
    },

    refreshAssetSetOctrees(viewerId: string, assetSets?: IAssetSet[], assetSetIsolation: boolean = false, requestId?: string) {
        const rid = requestId || _makeRequestId();
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_REFRESH_ASSET_SET_OCTREES,
            viewerId,
            assetSets,
            assetSetIsolation,
            requestId: rid
        });
        return rid;
    },

    setScanPositions(viewerId: string, scanPositions: IView360Detail[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_SCAN_POSITIONS,
            viewerId: viewerId,
            scanPositions: scanPositions,
        });
    },

    setSelectedAssetSet(viewerId: string, assetSet: IAssetSet) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_SELECTED_ASSET_SET,
            viewerId: viewerId,
            assetSet: assetSet,
        });
    },

    highlightScanPosition(viewerId: string, viewId: string | null) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_HIGHLIGHT_SCAN_POSITION,
            viewerId: viewerId,
            viewId: viewId,
        });
    },

    setMeasurements(viewerId: string, measurements: Measurement[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_MEASUREMENTS,
            viewerId: viewerId,
            measurements: measurements
        });
    },

    setAssemblyTemplates(viewerId: string, assemblyTemplates: IAssemblyTemplate[], callback: () => void) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_ASSEMBLY_TEMPLATES,
            viewerId: viewerId,
            assemblyTemplates: assemblyTemplates,
            callback: callback
        });
    },

    setAssemblies(viewerId: string, assemblies: IAssembly[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_ASSEMBLIES,
            viewerId: viewerId,
            assemblies: assemblies
        });
    },

    setAssemblyCreationTemplate(viewerId: string, assemblyCreationTemplate: IAssemblyTemplate) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_ASSEMBLY_CREATION_TEMPLATE,
            viewerId: viewerId,
            assemblyCreationTemplate: assemblyCreationTemplate
        });
    },

    setAssemblyEditMode(viewerId: string, mode: "move" | "scale" | "none") {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_ASSEMBLY_EDIT_MODE,
            viewerId: viewerId,
            mode: mode
        });
    },

    setTagEditMode(viewerId: string, mode: "move" | "scale" | "none") {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_TAG_EDIT_MODE,
            viewerId: viewerId,
            mode: mode
        });
    },

    setMeasurementType(viewerId: string, measurementType: MeasurementType) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_MEASUREMENT_TYPE,
            viewerId: viewerId,
            measurementType: measurementType
        });
    },

    setViewerClickMode(viewerId: string, mode: "select" | "point3D") {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_VIEWER_CLICK_MODE,
            viewerId: viewerId,
            mode: mode
        });
    },

    highlightAssets(viewerId: string, ids: string[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_HIGHLIGHT_ASSETS,
            viewerId: viewerId,
            ids: ids
        });
    },

    unhighlightObjects(viewerId: string, objectType: Enumerations.PickObjectType, ids: string[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_UNHIGHLIGHT_ASSETS,
            objectType: objectType,
            viewerId: viewerId,
            ids: ids
        });
    },

    unhighlightAllObjects(viewerId: string) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_UNHIGHLIGHT_ALL_ASSETS,
            viewerId: viewerId
        });
    },

    colorizeAssetSets(
        viewerId: string,
        assetSetData: { id: string; priority: number; colorKey?: number; hidden?: boolean; isAll3DAssets?: boolean; materialID?: string | null; tagIDs: string[]; }[],
        requestId?: string,
        strictIsolation: boolean = false
    ) {
        const rid = requestId || _makeRequestId();
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_COLORIZE_ASSET_SETS,
            viewerId: viewerId,
            assetSetData: assetSetData,
            strictIsolation,
            requestId: rid
        });
        return rid;
    },

    setAdvancedMaterialTunings(
        viewerId: string,
        tunings: Record<string, IAdvancedMaterialTuning>
    ) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_ADVANCED_MATERIAL_TUNINGS,
            viewerId,
            tunings
        });
    },

    destroyAllAssetSets(viewerId: string) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_DESTROY_ASSET_SETS,
            viewerId: viewerId
        });
    },

    populateAssetSets(viewerId: string, assetSetData: { id: string, assetIds: string[], isAll3DAssets: boolean }[], requestId?: string) {
        const rid = requestId || _makeRequestId();
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_POPULATE_ASSET_SETS,
            viewerId: viewerId,
            assetSetData: assetSetData,
            requestId: rid
        });
        return rid;
    },

    applyAssetSetMembershipDelta(
        viewerId: string,
        activatedSets: { id: string, assetIds: string[], isAll3DAssets: boolean }[],
        deactivatedSetIds: string[],
        requestId?: string
    ) {
        const rid = requestId || _makeRequestId();
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_APPLY_ASSET_SET_MEMBERSHIP_DELTA,
            viewerId,
            activatedSets,
            deactivatedSetIds,
            requestId: rid
        });
        return rid;
    },

    displayKVAssetSets(viewerId: string, assetSetData: IKnowledgeViewAssetSet[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_DISPLAY_KV_ASSET_SETS,
            viewerId: viewerId,
            assetSetData: assetSetData
        });
    },

    highlightAssetSetAssets(viewerId: string, ids: string[]) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_HIGHLIGHT_ASSET_SET_ASSETS,
            viewerId: viewerId,
            ids: ids
        });
    },

    focusCamera(viewerId: string, focusPosition: BABYLON.Vector3) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_FOCUS_CAMERA,
            viewerId: viewerId,
            focusPosition: focusPosition
        });
    },

    zoomCamera(viewerId: string, minZoom: IVector3D, maxZoom: IVector3D) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_ZOOM_CAMERA,
            viewerId: viewerId,
            minZoom: minZoom,
            maxZoom: maxZoom
        });
    },

    setCamera(
        viewerId: string,
        cameraPosition: BABYLON.Vector3,
        focusPosition: BABYLON.Vector3,
        skipAnimation: boolean,
        onComplete?: () => void,
    ) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_CAMERA,
            viewerId: viewerId,
            cameraPosition: cameraPosition,
            focusPosition: focusPosition,
            skipAnimation: skipAnimation,
            onComplete,
        });
    },

    setViewerConfiguration(viewerId: string, configuration: IFacilityConfig) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_VIEWER_CONFIGURATION,
            viewerId: viewerId,
            configuration: configuration
        });
    },

    setContentLayers(viewerId: string, layers: ContentLayers) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_CONTENT_LAYERS,
            viewerId: viewerId,
            layers: layers
        });
    },
    setViewerSettings(viewerId: string, settings: Record<string, any>) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_VIEWER_SETTINGS,
            viewerId: viewerId,
            settings: settings
        });
    },
    setShowOccludedTags(viewerId: string, show: boolean) {
        dispatcher.dispatch({
            actionType: ReactViewer3DConstants.RV_SET_SHOW_OCCLUDED_TAGS,
            viewerId: viewerId,
            show: show
        });
    }
}

export default ReactViewer3DActions;
